import React, { useEffect, useState } from "react";
import axios from "axios";
import Tooltip from "../../components/Tooltip";

type Option = {
  id: string;
  label: string;
  mode: "minutes" | "end_of_days" | "never";
  value: number | null;
  order: number;
  followSession?: boolean;
};

type OptionGroup = {
  title?: string;
  options: Option[];
};

const sessionOptions: Option[] = [
  { id: "end_of_days:0", label: "am selben Tag", mode: "end_of_days", value: 0, order: 0 },
  { id: "end_of_days:1", label: "am Folgetag", mode: "end_of_days", value: 1, order: 1 },
  { id: "end_of_days:2", label: "nach 2 Tagen", mode: "end_of_days", value: 2, order: 2 },
  { id: "end_of_days:3", label: "nach 3 Tagen", mode: "end_of_days", value: 3, order: 3 },
];

const audioOptions: Option[] = [
  { id: "minutes:0", label: "sofort", mode: "minutes", value: 0, order: -3 },
  { id: "minutes:30", label: "in 30 Minuten", mode: "minutes", value: 30, order: -2 },
  { id: "minutes:360", label: "in 6 Stunden", mode: "minutes", value: 360, order: -1 },
  { id: "end_of_days:0", label: "am selben Tag", mode: "end_of_days", value: 0, order: 0 },
  { id: "end_of_days:1", label: "am Folgetag", mode: "end_of_days", value: 1, order: 1 },
  { id: "end_of_days:2", label: "nach 2 Tagen", mode: "end_of_days", value: 2, order: 2 },
  { id: "end_of_days:3", label: "nach 3 Tagen", mode: "end_of_days", value: 3, order: 3 },
];

const audioOptionGroups: OptionGroup[] = [
  {
    title: "Kurzfristig",
    options: audioOptions.filter((option) => option.mode === "minutes"),
  },
  {
    title: "Tagesbasiert",
    options: audioOptions.filter((option) => option.mode !== "minutes"),
  },
];

const sessionOptionGroups: OptionGroup[] = [
  { options: sessionOptions },
];

const defaultValues = {
  audio: "end_of_days:3",
  session: "end_of_days:3",
};

const getOption = (options: Option[], id: string) => options.find((option) => option.id === id) ?? options[0];
const getOptionId = (options: Option[], config: { mode: string; value: number | null }) =>
  options.find((option) => option.mode === config.mode && option.value === config.value)?.id ?? options[0].id;

const RetentionSettings: React.FC = () => {
  const [values, setValues] = useState(defaultValues);
  const [initialValues, setInitialValues] = useState(defaultValues);
  const [highlightSessionLimit, setHighlightSessionLimit] = useState(false);

  useEffect(() => {
    axios.get("/api/apps/retention-settings/")
      .then((res) => {
        const nextValues = {
          audio: getOptionId(audioOptions, res.data.audio),
          session: getOptionId(sessionOptions, res.data.session),
        };
        setValues(nextValues);
        setInitialValues(nextValues);
      })
      .catch((error) => {
        console.error("Failed to load retention settings:", error);
      });
  }, []);

  const sessionOrder = getOption(sessionOptions, values.session).order;
  const isDirty =
    values.audio !== initialValues.audio ||
    values.session !== initialValues.session;

  const isStricterSelection = (options: Option[], nextId: string, previousId: string) =>
    getOption(options, nextId).order < getOption(options, previousId).order;

  const isDisabled = (option: Option, currentSessionOrder = sessionOrder) =>
    !option.followSession && currentSessionOrder !== 4 && option.order > currentSessionOrder;

  const clampSelection = (options: Option[], selected: string, currentSessionOrder: number) => {
    if (!isDisabled(getOption(options, selected), currentSessionOrder)) return selected;
    if (currentSessionOrder === 4) return "never";
    return options.find((option) => !option.followSession && option.order === currentSessionOrder)?.id ?? options[0].id;
  };

  const handleSessionChange = (session: string) => {
    setValues((current) => ({
      session,
      audio: clampSelection(audioOptions, current.audio, getOption(sessionOptions, session).order),
    }));
  };

  const save = () => {
    const becomesStricter =
      isStricterSelection(audioOptions, values.audio, initialValues.audio) ||
      isStricterSelection(sessionOptions, values.session, initialValues.session);

    if (
      becomesStricter &&
      !window.confirm(
        "Die neuen Löschfristen gelten auch für bereits vorhandene Sitzungen. Dadurch können Daten früher gelöscht werden als bisher. Möchten Sie die Änderungen speichern?"
      )
    ) {
      return;
    }

    axios.put("/api/apps/retention-settings/", {
      audio: getOption(audioOptions, values.audio),
      session: getOption(sessionOptions, values.session),
    }).then(() => {
      setInitialValues(values);
    });
  };

  const renderOptionCard = (
    option: Option,
    selected: string,
    name: string,
    onChange: (value: string) => void,
    disableBySession = true,
  ) => {
    const disabled = disableBySession ? isDisabled(option) : false;
    const isHighlightedSessionLimit = name === "sessionRetention" && option.id === values.session && highlightSessionLimit;

    const card = (
      <label
        key={`${name}-${option.id}`}
        htmlFor={`${name}-${option.id}`}
        className={[
          "relative flex items-start gap-2 rounded-md border px-2.5 py-2 transition-colors",
          disabled
            ? "border-gray-200 bg-gray-50 cursor-default"
            : "border-gray-200 bg-white hover:border-gray-300 cursor-pointer",
          selected === option.id ? "border-[#2E90B5] ring-1 ring-[#2E90B5]/20" : "",
          isHighlightedSessionLimit ? "border-[#2E90B5] bg-[#2E90B5]/5 ring-2 ring-[#2E90B5]/25" : "",
        ].join(" ")}
        onMouseEnter={() => {
          if (disabled && disableBySession) setHighlightSessionLimit(true);
        }}
        onMouseLeave={() => {
          if (disableBySession) setHighlightSessionLimit(false);
        }}
        onFocus={() => {
          if (disabled && disableBySession) setHighlightSessionLimit(true);
        }}
        onBlur={() => {
          if (disableBySession) setHighlightSessionLimit(false);
        }}
      >
        <input
          type="radio"
          id={`${name}-${option.id}`}
          name={name}
          className={`lumanoteEX__radio mt-0.5 shrink-0 ${disabled ? "cursor-default" : "cursor-pointer"}`}
          checked={selected === option.id}
          disabled={disabled}
          onChange={() => onChange(option.id)}
        />

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <span
              className={[
                "radio__group__each__label !leading-5 !text-[13px]",
                selected === option.id ? "!text-black" : "!text-gray",
                disabled ? "!text-gray-400" : "",
              ].join(" ")}
            >
              {option.label}
            </span>

            {disabled && (
              <span className="inline-flex shrink-0 items-center text-[11px] font-medium text-gray-400 pointer-events-none select-none">
                !
              </span>
            )}
            {!disabled && isHighlightedSessionLimit && (
              <span className="inline-flex shrink-0 items-center rounded-full bg-[#2E90B5]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#2E90B5]">
                Limit
              </span>
            )}
          </div>
        </div>
      </label>
    );

    return disabled ? (
      <Tooltip key={`${name}-${option.id}`} text="Limitiert durch Sitzung.">
        {card}
      </Tooltip>
    ) : card;
  };

  const renderOptionGroups = (
    groups: OptionGroup[],
    selected: string,
    name: string,
    onChange: (value: string) => void,
    gridClassName = "grid-cols-1 md:grid-cols-4",
    disableBySession = true,
  ) => (
    <div className="space-y-3">
      {groups.map((group, index) => (
        <div key={`${name}-group-${index}`} className="space-y-2">
          {group.title && (
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              {group.title}
            </div>
          )}
          <div className={`grid ${gridClassName} gap-2`}>
            {group.options.map((option) => renderOptionCard(option, selected, name, onChange, disableBySession))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="w-full h-[calc(100vh-10rem)] pt-2 pb-24 sm:pb-0 overflow-y-auto overflow-x-hidden px-4">
      <div className="w-full max-w-6xl pb-6">
        <header className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-5">
          <div className="space-y-1.5 max-w-3xl">
            <h3 className="font-bold text-2xl text-black">Löschfristen</h3>
            <p className="text-gray-500 text-sm leading-5">
              Legen Sie fest, wie lange sensible Daten automatisch gespeichert bleiben. Wenn sie nicht mehr benötigt werden, ist es am sichersten, sie früher löschen zu lassen.
            </p>
            <ul className="m-0 list-none space-y-1 p-0 text-sm text-gray-500 pointer-events-none">
              <li className="flex items-start gap-2 bg-transparent">
                <span className="mt-0.5 text-gray-300">•</span>
                <span>gilt ab dem Erstellungszeitpunkt der Sitzung</span>
              </li>
              <li className="flex items-start gap-2 bg-transparent">
                <span className="mt-0.5 text-gray-300">•</span>
                <span>tagesbasierte Löschfristen werden zum Tagesende angewendet</span>
              </li>
              <li className="flex items-start gap-2 bg-transparent">
                <span className="mt-0.5 text-gray-300">•</span>
                <span>Änderungen gelten auch für bereits vorhandene Sitzungen</span>
              </li>
            </ul>
          </div>
          <div className="bg-white w-full md:w-auto fixed bottom-0 left-0 px-3 py-4 flex items-center justify-end md:p-0 md:bg-transparent md:static z-10">
            <button
              className="_button_19rto_40 _purple_19rto_126 _filled_19rto_80 px-9 w-full md:w-auto"
              disabled={!isDirty}
              onClick={save}
            >
              <div className="_btnFlex_19rto_32">Speichern</div>
            </button>
          </div>
        </header>

        <main className="space-y-4">
          <section className="rounded-xl border border-gray-200 bg-white p-4 md:p-5">
            <div className="grid gap-4 md:grid-cols-[280px_minmax(0,1fr)] md:items-start">
              <div className="space-y-2">
                <div className="flex min-h-7 items-center gap-2">
                  <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600 self-center">
                    <svg viewBox="0 0 20 20" fill="none" className="block h-4 w-4" aria-hidden="true">
                      <path d="M10 3a3 3 0 0 0-3 3v4a3 3 0 1 0 6 0V6a3 3 0 0 0-3-3Z" stroke="currentColor" strokeWidth="1.5" />
                      <path d="M5 9.5V10a5 5 0 0 0 10 0v-.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      <path d="M10 15v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      <path d="M7.5 17h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </span>
                  <h4 className="m-0 font-semibold text-[15px] leading-[1] text-black">Audioaufnahme</h4>
                </div>
                <span className="text-black !font-normal block text-sm">Audio löschen nach</span>
                <ul className="m-0 list-none space-y-1.5 p-0 text-sm text-gray-600">
                  <li className="m-0 flex items-start gap-2 border-0 bg-transparent p-0 shadow-none pointer-events-none">
                    <span className="mt-0.5 bg-transparent p-0 text-gray-400 shadow-none">•</span>
                    <span>Audioaufnahme wird vollständig entfernt.</span>
                    </li>
                                      <li className="m-0 flex items-start gap-2 border-0 bg-transparent p-0 shadow-none pointer-events-none">

                    <span className="mt-0.5 bg-transparent p-0 text-gray-400 shadow-none">•</span>
                    <span>Audioaufnahme steht nicht mehr zum Download bereit.</span>
                  </li>
                </ul>
              </div>
              {renderOptionGroups(audioOptionGroups, values.audio, "audioRetention", (audio) => setValues((current) => ({ ...current, audio })), "grid-cols-2 md:grid-cols-4")}
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-4 md:p-5">
            <div className="grid gap-4 md:grid-cols-[280px_minmax(0,1fr)] md:items-start">
              <div className="space-y-2">
                <div className="flex min-h-7 items-center gap-2">
                  <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-500 self-center">
                    <svg viewBox="0 0 20 20" fill="none" className="block h-4 w-4" aria-hidden="true">
                      <rect x="4.5" y="3.5" width="11" height="13" rx="2" stroke="currentColor" strokeWidth="1.5" />
                      <path d="M7 3.5v-1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      <path d="M13 3.5v-1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      <path d="M4.5 7.5h11" stroke="currentColor" strokeWidth="1.5" />
                    </svg>
                  </span>
                  <h4 className="m-0 font-semibold text-[15px] leading-[1] text-black">Sitzung</h4>
                </div>
                <span className="text-black !font-normal block text-sm">Sitzung löschen nach</span>
                <ul className="m-0 list-none space-y-1.5 p-0 text-sm text-gray-600">
                  <li className="m-0 flex items-start gap-2 border-0 bg-transparent p-0 shadow-none pointer-events-none">
                    <span className="mt-0.5 bg-transparent p-0 text-red-400 shadow-none">•</span>
                    <span>Audioaufnahme wird vollständig entfernt.</span>
                  </li>
                  <li className="m-0 flex items-start gap-2 border-0 bg-transparent p-0 shadow-none pointer-events-none">
                    <span className="mt-0.5 bg-transparent p-0 text-red-400 shadow-none">•</span>
                    <span>Transkript wird vollständig entfernt.</span>
                  </li>
                  <li className="m-0 flex items-start gap-2 border-0 bg-transparent p-0 shadow-none pointer-events-none">
                    <span className="mt-0.5 bg-transparent p-0 text-red-400 shadow-none">•</span>
                    <span>Dokumentation wird vollständig aus dem System gelöscht.</span>
                  </li>
                </ul>
              </div>
              {renderOptionGroups(sessionOptionGroups, values.session, "sessionRetention", handleSessionChange, "grid-cols-2 md:grid-cols-4", false)}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default RetentionSettings;
